// summaryProjection — the client-side read model for the workspace-wide
// semantic summary (IDD 94442b9d): Workspace.summaryConsent + the
// `annotation_placed` summary anchored at `Actor.authorRef`
// (`packages/app/src/semanticSummary.ts`).
//
// READ PATH CLOSED: `GET /state` (`packages/server/src/routes/state.ts`)
// now serves BOTH `Workspace.summaryConsent` and the latest generated
// version's prose + timestamp as `value.summary`
// (`App.showSemanticSummary`, `packages/app/src/workflows/
// showSemanticSummary.ts`), projected into `Library.summary` by
// `httpPorts/workspace.ts`. The server projection is the SOURCE OF TRUTH
// for both fields now — `situationSummary` reads them straight
// off the loaded `Library`, never the device-local consent mirror
// (`app/summaryConsent.ts`, kept only as `useShellFoundation`'s
// instant-paint cache / offline fallback for the separate Settings toggle
// cell, synced from this same server read).

import type { SituationSummaryState } from "../components/WorkspaceLanding/WorkspaceLanding.js";
import type { LibrarySemanticSummary } from "./ports.js";
import { formatRecency } from "./recency.js";

export { situationSummary };

// The compact line shows only the summary's FIRST sentence — a calm one-line
// scent between the greeting and the composer on the hero. The full prose
// lives in the Journal ("Show more" navigates there).
const firstSentence = (text: string): string => {
    const firstLine = text.trim().split(/\r?\n/, 1)[0] ?? "";
    const end = firstLine.search(/[.!?](\s|$)/);
    return end === -1 ? firstLine : firstLine.slice(0, end + 1);
};

// `composedAt` arrives off the wire as a raw ISO instant; the landing never
// touches Date, so it is PRE-FORMATTED here. `nowMs` + `locale` are the impure
// host's clock/i18n reads, threaded in so this stays pure (the same split
// `recency.ts` documents). An unparseable instant falls back to the raw string.
const situationSummary = (
    summary: LibrarySemanticSummary,
    nowMs: number,
    locale: string,
): SituationSummaryState =>
    !summary.consentEnabled
        ? { type: "consent-off" }
        : summary.current === null
          ? { type: "first-run" }
          : {
                type: "summarized",
                excerpt: firstSentence(summary.current.text),
                composedAt:
                    formatRecency(summary.current.composedAt, nowMs, locale)
                        ?.absolute ?? summary.current.composedAt,
            };
